import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import moment from "moment";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import CaseStatusChip from "./CaseStatusChip";
import ReportIssueDialog from "./ReportIssueDialog";
import useSupportCaseOptions from "../../hooks/useSupportCaseOptions";
import { apiGetMySupportCases, supportCaseErrorMessage } from "../../api/supportCaseApi";
import {
  readCaseList,
  readCasePagination,
  forCustomerDisplay,
  caseCategoryLabel,
} from "../../utils/supportCaseVocabulary";

/**
 * SupportCaseList: the customer's own reported issues.
 *
 * Chrome free on purpose, so it can be mounted twice without a second copy of
 * the list: inside the account panel through Sidebar/SidebarData.js, and as a
 * standalone page at /support-cases through pages/SupportCasesPage.jsx.
 *
 * The server scopes the list to the caller, so this never sends a customer id
 * and never filters by one. Every row goes through forCustomerDisplay first,
 * which keeps internal notes, assignment internals and the money on a
 * resolution out of component state entirely.
 *
 * REPORTING FROM HERE. The list carries its own "Report an issue" action, with
 * no order attached, because a customer whose problem is not about an order had
 * nowhere else to start: the only other trigger in this client hangs off an
 * order detail screen. It opens the SAME ReportIssueDialog that screen opens,
 * with no `order` prop, so there is one create form in this tree and this
 * action adds a door to it rather than a copy of it.
 */
const PAGE_LIMIT = 20;

const SupportCaseList = ({ title = "My reported issues" }) => {
  const history = useHistory();
  const { statuses } = useSupportCaseOptions();

  const [cases, setCases] = useState([]);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ pages: 1, total: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reporting, setReporting] = useState(false);
  /* Bumped when a case is created and we stay on this screen, so the fetch
     below re-runs and the new case is in the list the customer is looking at
     rather than appearing only after a manual reload. */
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    const params = { page, limit: PAGE_LIMIT, sort: "-lastActivityAt" };
    if (status) params.status = status;
    apiGetMySupportCases(params)
      .then((data) => {
        if (!alive) return;
        const rows = readCaseList(data).map(forCustomerDisplay).filter(Boolean);
        setCases(rows);
        setPageInfo(readCasePagination(data, rows.length));
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(supportCaseErrorMessage(err, "We could not load your reported issues."));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [page, status, reloadKey]);

  const changeStatus = (next) => {
    setStatus(next);
    setPage(1);
  };

  /* Land on the case that was just opened, the same way the order screen's
     trigger does: a report that vanishes into a toast is indistinguishable
     from one that was never sent. With no id to land on, refresh in place. */
  const handleCreated = (created) => {
    if (created?._id) {
      history.push(`/support-cases/${created._id}`);
      return;
    }
    setStatus("");
    setPage(1);
    setReloadKey((key) => key + 1);
  };

  return (
    <Wrap>
      <Head>
        <div>
          <h2>{title}</h2>
          <Sub>
            Every issue you have reported, with where it stands. Open one to read
            the replies or add something.
          </Sub>
        </div>
        <HeadActions>
          <Filter
            value={status}
            aria-label="Filter by status"
            onChange={(e) => changeStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            {statuses.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Filter>
          <NewCase type="button" onClick={() => setReporting(true)}>
            <FlagOutlinedIcon fontSize="small" />
            Report an issue
          </NewCase>
        </HeadActions>
      </Head>

      {loading && <Muted>Loading your reported issues...</Muted>}
      {!loading && error && <Muted>{error}</Muted>}

      {!loading && !error && cases.length === 0 && (
        <Empty>
          <strong>Nothing reported yet</strong>
          <span>
            {status
              ? "No issue of yours is in that status right now."
              : "Report an issue above and it will appear here. You can also report one from an order, which attaches that order for you."}
          </span>
        </Empty>
      )}

      <List>
        {cases.map((row) => (
          <Row
            key={String(row._id)}
            onClick={() => history.push(`/support-cases/${row._id}`)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") history.push(`/support-cases/${row._id}`);
            }}
          >
            <Main>
              <TopLine>
                <CaseNumber>{row.caseNumber}</CaseNumber>
                <CaseStatusChip status={row.status} size="sm" />
              </TopLine>
              <Subject>{row.subject}</Subject>
              <Meta>
                {caseCategoryLabel(row.category)}
                {row.order?.number ? ` on order ${row.order.number}` : ""}
                {row.vendor?.name ? ` with ${row.vendor.name}` : ""}
              </Meta>
              <Meta>
                Last activity{" "}
                {row.lastActivityAt ? moment(row.lastActivityAt).fromNow() : "just now"}
              </Meta>
            </Main>
            <Open>View</Open>
          </Row>
        ))}
      </List>

      <ReportIssueDialog
        open={reporting}
        onClose={() => setReporting(false)}
        onCreated={handleCreated}
      />

      {pageInfo.pages > 1 && (
        <Pager>
          <PageBtn type="button" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
            Previous
          </PageBtn>
          <PageAt>
            Page {page} of {pageInfo.pages}
          </PageAt>
          <PageBtn
            type="button"
            disabled={page >= pageInfo.pages || loading}
            onClick={() => setPage(page + 1)}
          >
            Next
          </PageBtn>
        </Pager>
      )}
    </Wrap>
  );
};

const Wrap = styled.div`
  padding: 8px 4px 24px;

  h2 {
    font-size: 22px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 4px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Sub = styled.div`
  color: #6b7280;
  font-size: 13.5px;
  max-width: 460px;
  line-height: 1.5;
`;

const HeadActions = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const NewCase = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  border-radius: 9px;
  background: #5d8f24;
  color: #ffffff;
  font-size: 13.5px;
  font-weight: 700;
  padding: 9px 15px;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #4f7a1e;
  }
`;

const Filter = styled.select`
  flex: none;
  border: 1px solid #d8dbde;
  border-radius: 9px;
  padding: 9px 11px;
  font-size: 13.5px;
  color: #374151;
  background: #ffffff;
  font-family: inherit;
`;

const Muted = styled.div`
  color: #6b7280;
  padding: 18px 0;
  font-size: 14px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px dashed #dfe3e6;
  border-radius: 12px;
  padding: 26px 22px;
  color: #6b7280;
  font-size: 13.5px;
  line-height: 1.55;

  strong {
    color: #1f2937;
    font-size: 15px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #eef0f2;
  border-radius: 12px;
  padding: 15px 18px;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const TopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 4px;
`;

const CaseNumber = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #9ca3af;
`;

const Subject = styled.div`
  font-weight: 700;
  color: #1f2937;
  font-size: 14.5px;
  word-break: break-word;
`;

const Meta = styled.div`
  font-size: 12.5px;
  color: #6b7280;
  margin-top: 3px;
`;

const Open = styled.div`
  flex: none;
  font-size: 12.5px;
  font-weight: 700;
  color: #5d8f24;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 18px;
`;

const PageBtn = styled.button`
  border: 1px solid #d8dbde;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;

  &:disabled {
    color: #b8bcc0;
    cursor: default;
  }
`;

const PageAt = styled.div`
  font-size: 12.5px;
  color: #6b7280;
`;

export default SupportCaseList;
